import React, { Fragment, useRef } from "react";
import { useTranslate } from "react-admin";
import {
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Search,
  SearchProps,
  SearchPanelProps,
  useSearchResults,
  groupSearchResultsByResource,
  useArrowKeysToNavigate,
} from "src/enterprise/ra-search";
import {
  ArticleListItem,
  CustomerListItem,
  OrderListItem,
  PageListItem,
  ProductListItem,
} from ".";

const CustomSearch = (props: SearchProps) => {
  return (
    <div data-testid="search">
      <Search {...props} sx={{ mt: 0 }}>
        <CustomSearchPanel />
      </Search>
    </div>
  );
};

export default CustomSearch;

const CustomSearchPanel = (props: SearchPanelProps) => {
  const listRef = useRef<HTMLUListElement>(
    null
  ) as React.MutableRefObject<HTMLUListElement>;
  const translate = useTranslate();
  useArrowKeysToNavigate(listRef);
  const { data, onClose } = useSearchResults();

  if (!data || data.length === 0) {
    return (
      <List data-testid="search-panel" dense {...props}>
        <ListItem>
          <ListItemText primary={translate("ra.navigation.no_results")} />
        </ListItem>
      </List>
    );
  }
  const groupedData = groupSearchResultsByResource(data, translate);

  return (
    <List
      sx={{
        maxHeight: "calc(100vh - 100px)",
        maxWidth: 512,
        minWidth: 215,
        overflowX: "hidden",
      }}
      data-testid="search-panel"
      dense
      ref={listRef}
      {...props}
    >
      {groupedData.map((group) => (
        <Fragment key={group.label}>
          <ListSubheader
            role="presentation"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1,
              px: 2,
            }}
            disableSticky
          >
            <>
              <Typography
                sx={{
                  textTransform: "capitalize",
                }}
                variant="subtitle2"
              >
                {group.label.toString() === "Admin/users"
                  ? "Customers"
                  : group.label.toString().substring(6)}
              </Typography>
            </>
          </ListSubheader>
          {group.data.map((searchResultItem) => (
            <CustomSearchResultItem
              key={searchResultItem.id}
              data={searchResultItem}
              onClose={onClose}
            />
          ))}
        </Fragment>
      ))}
    </List>
  );
};

const CustomSearchResultItem = (props: any) => {
  const { data, onClose, ...rest } = props;
  if (!data) {
    return null;
  }
  switch (data.type) {
    case "admin/users":
      return <CustomerListItem {...rest} data={data} onClick={onClose} />;
    case "admin/pages":
      return <PageListItem {...rest} data={data} onClick={onClose} />;
    case "admin/products":
      return <ProductListItem {...rest} data={data} onClick={onClose} />;
    case "admin/orders":
      return <OrderListItem {...rest} data={data} onClick={onClose} />;
    case "admin/articles":
      return <ArticleListItem {...rest} data={data} onClick={onClose} />;
    default:
      return null;
  }
};
